/**
 * SpinalEaseFamilyCircle — Multi-voice management for the Spinal Ease
 * post-op recovery experience.
 *
 * A recipient (e.g. mum recovering from surgery) can have multiple
 * loved ones each contribute a labeled voice recording. She then
 * chooses whose voice guides today's session — her niece Daisy, her
 * nephew Charlie, her daughter Sarah, or the Michael V3 studio voice.
 *
 * Two modes:
 *   - target_email specified → uploader is adding voices for someone
 *     else (e.g. daughter uploading for mum). Shows "Adding voices to
 *     mum@example.com's Family Circle".
 *   - No target_email → the user manages their OWN voices.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Square, Upload, Play, Pause, Trash2, X, ChevronDown, ChevronUp, UserPlus, Check, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const token = localStorage.getItem('reclaim_token') || localStorage.getItem('token') || localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const RELATIONS = ['Daughter', 'Son', 'Niece', 'Nephew', 'Granddaughter', 'Grandson', 'Sister', 'Brother', 'Partner', 'Friend', 'Other'];

const SpinalEaseFamilyCircle = ({ onClose, initialTargetEmail = '' }) => {
  const [targetEmail, setTargetEmail] = useState(initialTargetEmail);
  const [scriptExpanded, setScriptExpanded] = useState(false);
  const [script, setScript] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [voices, setVoices] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgKind, setMsgKind] = useState('info'); // 'info' | 'error' | 'success'

  // New-voice form
  const [showAddForm, setShowAddForm] = useState(false);
  const [speakerName, setSpeakerName] = useState('');
  const [relation, setRelation] = useState('');

  // Recording state
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Preview
  const previewAudio = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  // Daily Healing Messages
  const [dmessages, setDmessages] = useState([]);
  const [showMsgForm, setShowMsgForm] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [msgSpeaker, setMsgSpeaker] = useState('');
  const [msgRecording, setMsgRecording] = useState(false);
  const [msgRecordSeconds, setMsgRecordSeconds] = useState(0);
  const msgMediaRef = useRef(null);
  const msgChunksRef = useRef([]);
  const msgTimerRef = useRef(null);
  const msgPreviewRef = useRef(null);
  const [msgPlayingId, setMsgPlayingId] = useState(null);

  const notify = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };

  const loadScript = async () => {
    try {
      const r = await fetch(`${API}/api/healing/spinal-ease/voice-script`);
      const d = await r.json();
      setScript(d.script || '');
      setWordCount(d.word_count || 0);
    } catch (e) { console.error('Failed to load script', e); }
  };

  const loadVoices = async () => {
    const qs = targetEmail ? `?target_email=${encodeURIComponent(targetEmail)}` : '';
    try {
      const r = await fetch(`${API}/api/healing/spinal-ease/family/voices${qs}`, { headers: authHeader() });
      if (!r.ok) {
        if (r.status === 404) notify('That email is not a Reclaim account yet.', 'error');
        return;
      }
      const d = await r.json();
      setVoices(d.voices || []);
      setSelectedId(d.selected_voice_id || null);
    } catch (e) { console.error('Failed to load voices', e); }
  };

  useEffect(() => { loadScript(); loadVoices(); loadMessages(); }, []);
  useEffect(() => { loadVoices(); loadMessages(); }, [targetEmail]);

  async function loadMessages() {
    const qs = targetEmail ? `?target_email=${encodeURIComponent(targetEmail)}` : '';
    try {
      const r = await fetch(`${API}/api/healing/spinal-ease/messages${qs}`, { headers: authHeader() });
      if (r.ok) {
        const d = await r.json();
        setDmessages(d.messages || []);
      }
    } catch { /* silent */ }
  }

  const startMsgRecording = async () => {
    if (!msgSpeaker.trim()) { notify('Add your name first.', 'error'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      msgChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) msgChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(msgChunksRef.current, { type: mime });
        stream.getTracks().forEach(t => t.stop());
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, `msg-${Date.now()}.webm`);
          fd.append('speaker_name', msgSpeaker);
          if (msgText.trim()) fd.append('text', msgText.trim());
          if (targetEmail) fd.append('target_email', targetEmail);
          const r = await fetch(`${API}/api/healing/spinal-ease/messages`, { method: 'POST', headers: authHeader(), body: fd });
          if (!r.ok) throw new Error('Message upload failed');
          notify(`Message from ${msgSpeaker} added.`, 'success');
          setMsgText(''); setMsgSpeaker(''); setShowMsgForm(false);
          await loadMessages();
        } catch (e) { notify(e.message, 'error'); }
        finally { setBusy(false); }
      };
      msgMediaRef.current = rec;
      rec.start();
      setMsgRecording(true);
      setMsgRecordSeconds(0);
      msgTimerRef.current = setInterval(() => setMsgRecordSeconds(s => s + 1 <= 15 ? s + 1 : (rec.stop(), 15)), 1000);
    } catch { notify('Microphone permission was declined.', 'error'); }
  };

  const stopMsgRecording = () => {
    if (!msgMediaRef.current) return;
    try { msgMediaRef.current.stop(); } catch { /* stopped */ }
    setMsgRecording(false);
    if (msgTimerRef.current) { clearInterval(msgTimerRef.current); msgTimerRef.current = null; }
  };

  const previewMessage = (m) => {
    if (msgPreviewRef.current) { msgPreviewRef.current.pause(); msgPreviewRef.current = null; }
    if (msgPlayingId === m.id) { setMsgPlayingId(null); return; }
    const url = m.audio_url.startsWith('http') ? m.audio_url : `${API}${m.audio_url}`;
    const a = new Audio(url);
    a.onended = () => setMsgPlayingId(null);
    a.play().catch(() => notify('Could not play message.', 'error'));
    msgPreviewRef.current = a;
    setMsgPlayingId(m.id);
  };

  const deleteMessage = async (m) => {
    if (!window.confirm(`Remove ${m.speaker_name}'s message?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/healing/spinal-ease/messages/${m.id}`, { method: 'DELETE', headers: authHeader() });
      if (!r.ok) throw new Error('Delete failed');
      if (msgPlayingId === m.id && msgPreviewRef.current) { msgPreviewRef.current.pause(); }
      setMsgPlayingId(null);
      notify('Message removed.', 'success');
      await loadMessages();
    } catch (e) { notify(e.message, 'error'); }
    finally { setBusy(false); }
  };

  // Auto-poll while any voice is still refining so the badge switches
  // from "Refining…" to "Smooth" without the user having to close/reopen.
  useEffect(() => {
    const anyRefining = voices.some(v => !v.is_synthetic && !v.refined_via_elevenlabs);
    if (!anyRefining) return;
    const t = setInterval(loadVoices, 8000);
    return () => clearInterval(t);
  }, [voices]);

  const startRecording = async () => {
    if (!speakerName.trim()) { notify('Add a name for this voice first.', 'error'); return; }
    setMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        stream.getTracks().forEach(t => t.stop());
        await uploadBlob(blob, `voice-${Date.now()}.webm`);
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
      setRecordSeconds(0);
      timerRef.current = setInterval(() => setRecordSeconds(s => s + 1), 1000);
    } catch {
      notify('Microphone permission was declined.', 'error');
    }
  };

  const stopRecording = () => {
    if (!mediaRef.current) return;
    try { mediaRef.current.stop(); } catch { /* recorder already stopped */ }
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const uploadBlob = async (blob, filename) => {
    setBusy(true);
    notify('Uploading…');
    try {
      const fd = new FormData();
      fd.append('file', blob, filename);
      fd.append('speaker_name', speakerName);
      if (relation) fd.append('relation', relation);
      if (targetEmail) fd.append('target_email', targetEmail);
      const r = await fetch(`${API}/api/healing/spinal-ease/family/voices`, {
        method: 'POST', headers: authHeader(), body: fd,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Upload failed');
      }
      notify(`${speakerName}'s voice added to the Family Circle.`, 'success');
      setSpeakerName(''); setRelation(''); setShowAddForm(false);
      await loadVoices();
    } catch (e) {
      notify(e.message || 'Upload failed', 'error');
    } finally { setBusy(false); }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!speakerName.trim()) { notify('Add a name for this voice first.', 'error'); e.target.value = ''; return; }
    if (f.size > 60 * 1024 * 1024) { notify('File too large (max 60 MB).', 'error'); return; }
    await uploadBlob(f, f.name);
    e.target.value = '';
  };

  const preview = (voice) => {
    if (previewAudio.current) { previewAudio.current.pause(); previewAudio.current = null; }
    if (playingId === voice.id) { setPlayingId(null); return; }
    const url = voice.audio_url.startsWith('http') ? voice.audio_url : `${API}${voice.audio_url}`;
    const a = new Audio(url);
    a.onended = () => setPlayingId(null);
    a.play().catch(() => notify('Could not play recording.', 'error'));
    previewAudio.current = a;
    setPlayingId(voice.id);
  };

  const removeVoice = async (voice) => {
    if (voice.is_synthetic) { notify('Michael V3 is the studio default and can\'t be removed.', 'info'); return; }
    if (!window.confirm(`Remove ${voice.speaker_name}'s voice?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/healing/spinal-ease/family/voices/${voice.id}`, {
        method: 'DELETE', headers: authHeader(),
      });
      if (!r.ok) throw new Error('Delete failed');
      if (playingId === voice.id) { if (previewAudio.current) previewAudio.current.pause(); setPlayingId(null); }
      notify('Voice removed.', 'success');
      await loadVoices();
    } catch (e) { notify(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const selectVoice = async (voice) => {
    // Only allow selection when managing own circle (no target email).
    if (targetEmail) { notify('Selection is done by the recipient, not the giver.', 'info'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/healing/spinal-ease/family/select`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice_id: voice.id }),
      });
      if (!r.ok) throw new Error('Selection failed');
      setSelectedId(voice.id);
      notify(`Today, ${voice.speaker_name}'s voice will guide your session.`, 'success');
    } catch (e) { notify(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const isGiverMode = !!targetEmail;

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
      style={{ background: 'rgba(4,6,4,0.78)', backdropFilter: 'blur(18px)' }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="family-circle-panel"
    >
      <motion.div
        className="w-full max-w-2xl rounded-3xl p-6 md:p-8 max-h-[92vh] overflow-y-auto"
        style={{
          background: 'linear-gradient(180deg, rgba(20,18,14,0.98) 0%, rgba(12,10,8,0.98) 100%)',
          border: '1px solid rgba(200,170,110,0.16)',
        }}
        initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-white/25 text-[10px] tracking-[0.3em] uppercase mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Healing Circle
            </p>
            <h3 className="text-white/85 text-xl md:text-2xl" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              Whose voice will hold today?
            </h3>
            <p className="text-white/40 text-xs mt-1.5 max-w-md" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Gather the voices of everyone she loves. Each session opens with the Healing Circle around her.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white/80 p-1 -m-1"
            data-testid="family-panel-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Target-email switcher */}
        <div className="mb-5 rounded-2xl p-4" style={{
          background: isGiverMode ? 'rgba(140,170,120,0.06)' : 'rgba(255,255,255,0.03)',
          border: `1px solid ${isGiverMode ? 'rgba(140,170,120,0.18)' : 'rgba(255,255,255,0.06)'}`,
        }}>
          <label className="block text-white/45 text-[10px] tracking-[0.2em] uppercase mb-2"
            style={{ fontFamily: "'Manrope', sans-serif" }}>
            {isGiverMode ? 'Adding voices for' : 'Manage voices for'}
          </label>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="Leave blank to manage your own · or enter a recipient's email"
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg text-sm text-white/80 outline-none"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                fontFamily: "'Manrope', sans-serif",
              }}
              data-testid="family-target-email"
            />
          </div>
          {isGiverMode && (
            <p className="text-white/40 text-[11px] mt-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
              She chooses which voice plays — you can send several, and she picks one each day.
            </p>
          )}
        </div>

        {/* Script toggle */}
        <div className="rounded-2xl mb-5" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            onClick={() => setScriptExpanded(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-white/70"
            data-testid="family-script-toggle"
          >
            <span className="text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Script to read <span className="text-white/30">({wordCount} words · ~5 min)</span>
            </span>
            {scriptExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          <AnimatePresence>
            {scriptExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="px-4 pb-4"
              >
                <p className="text-white/60 text-sm leading-relaxed whitespace-pre-wrap"
                  style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                  data-testid="family-script-text">
                  {script}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Daily Healing Messages — short voice notes played randomly at session start */}
        <div className="mb-5 rounded-2xl p-4" style={{
          background: 'rgba(200,170,110,0.04)',
          border: '1px solid rgba(200,170,110,0.15)',
        }} data-testid="messages-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/60 text-[11px] tracking-[0.2em] uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Daily Messages · {dmessages.length}
            </p>
            {!showMsgForm && (
              <button
                onClick={() => setShowMsgForm(true)}
                className="text-white/70 text-xs px-3 py-1 rounded-full"
                style={{ background: 'rgba(200,170,110,0.12)', border: '1px solid rgba(200,170,110,0.25)' }}
                data-testid="messages-add-btn"
              >
                + Add message
              </button>
            )}
          </div>
          <p className="text-white/35 text-[11px] mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Short 5–15 second voice notes. One plays at random when she opens each session.
          </p>

          {dmessages.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {dmessages.map((m) => (
                <div key={m.id} className="flex items-center gap-2 rounded-lg px-3 py-2"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                  data-testid={`message-row-${m.id}`}>
                  <button onClick={() => previewMessage(m)} className="text-white/60 hover:text-white/90 p-1"
                    data-testid={`message-preview-${m.id}`}>
                    {msgPlayingId === m.id ? <Pause size={12} /> : <Play size={12} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/80 text-xs truncate" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                      {m.speaker_name || 'Loved one'}
                    </p>
                    {m.text && (
                      <p className="text-white/40 text-[10px] truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                        "{m.text}"
                      </p>
                    )}
                  </div>
                  <button onClick={() => deleteMessage(m)} className="text-white/25 hover:text-red-300 p-1"
                    data-testid={`message-remove-${m.id}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <AnimatePresence>
            {showMsgForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 pt-3 border-t border-white/10"
                data-testid="message-add-form"
              >
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input
                    type="text" value={msgSpeaker} onChange={(e) => setMsgSpeaker(e.target.value)}
                    placeholder="Your name (e.g. Daisy)"
                    className="px-3 py-2 rounded-lg text-sm text-white/85 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', fontFamily: "'Manrope', sans-serif" }}
                    data-testid="message-speaker"
                  />
                  <input
                    type="text" value={msgText} onChange={(e) => setMsgText(e.target.value.slice(0, 100))}
                    placeholder="Optional caption (e.g. Love you Mum)"
                    className="px-3 py-2 rounded-lg text-sm text-white/85 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', fontFamily: "'Manrope', sans-serif" }}
                    data-testid="message-text"
                  />
                </div>
                <div className="flex gap-2">
                  {!msgRecording ? (
                    <button
                      onClick={startMsgRecording}
                      disabled={busy || !msgSpeaker.trim()}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm disabled:opacity-40"
                      style={{ background: 'rgba(200,170,110,0.14)', border: '1px solid rgba(200,170,110,0.28)', color: 'rgba(255,255,255,0.8)', fontFamily: "'Manrope', sans-serif" }}
                      data-testid="message-record-btn"
                    >
                      <Mic size={13} /> Record (5–15s)
                    </button>
                  ) : (
                    <button
                      onClick={stopMsgRecording}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm"
                      style={{ background: 'rgba(200,80,80,0.15)', border: '1px solid rgba(220,100,100,0.4)', color: 'rgba(255,180,180,0.9)', fontFamily: "'Manrope', sans-serif" }}
                      data-testid="message-stop-btn"
                    >
                      <Square size={11} fill="currentColor" /> Stop · {msgRecordSeconds}s
                    </button>
                  )}
                  <button
                    onClick={() => { setShowMsgForm(false); setMsgSpeaker(''); setMsgText(''); }}
                    className="text-white/40 text-xs px-3"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                    data-testid="message-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Voice list */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/55 text-[11px] tracking-[0.25em] uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Voices in the circle · {voices.length}
            </p>
            {!showAddForm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-white/75"
                style={{ background: 'rgba(200,170,110,0.14)', border: '1px solid rgba(200,170,110,0.28)' }}
                data-testid="family-add-voice-btn"
              >
                <UserPlus size={11} /> Add a voice
              </button>
            )}
          </div>

          {voices.length === 0 ? (
            <div className="rounded-2xl px-4 py-6 text-center" style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px dashed rgba(255,255,255,0.08)',
            }}>
              <p className="text-white/40 text-sm" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                No voices yet — the circle is empty.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {voices.map((v) => (
                <VoiceRow
                  key={v.id}
                  voice={v}
                  isSelected={v.id === selectedId}
                  isPlaying={playingId === v.id}
                  onPreview={() => preview(v)}
                  onSelect={() => selectVoice(v)}
                  onRemove={() => removeVoice(v)}
                  canSelect={!isGiverMode}
                />
              ))}
            </div>
          )}
        </div>

        {/* Add-voice form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="rounded-2xl p-4 mb-4"
              style={{
                background: 'rgba(200,170,110,0.06)',
                border: '1px solid rgba(200,170,110,0.2)',
              }}
              data-testid="family-add-form"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/70 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  New voice
                </p>
                <button
                  onClick={() => { setShowAddForm(false); setSpeakerName(''); setRelation(''); }}
                  className="text-white/40 hover:text-white/70"
                  data-testid="family-cancel-add"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  value={speakerName}
                  onChange={(e) => setSpeakerName(e.target.value)}
                  placeholder="Name (e.g. Daisy)"
                  className="px-3 py-2 rounded-lg text-sm text-white/85 outline-none"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    fontFamily: "'Manrope', sans-serif",
                  }}
                  data-testid="family-speaker-name"
                />
                <select
                  value={relation}
                  onChange={(e) => setRelation(e.target.value)}
                  className="px-3 py-2 rounded-lg text-sm text-white/85 outline-none"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    fontFamily: "'Manrope', sans-serif",
                  }}
                  data-testid="family-relation"
                >
                  <option value="" style={{ background: '#151310' }}>Relation (optional)</option>
                  {RELATIONS.map(r => <option key={r} value={r} style={{ background: '#151310' }}>{r}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {!recording ? (
                  <motion.button
                    onClick={startRecording}
                    disabled={busy || !speakerName.trim()}
                    whileTap={{ scale: 0.97 }}
                    className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm disabled:opacity-40"
                    style={{
                      background: 'rgba(200,170,110,0.14)',
                      border: '1px solid rgba(200,170,110,0.28)',
                      color: 'rgba(255,255,255,0.8)',
                      fontFamily: "'Manrope', sans-serif",
                    }}
                    data-testid="family-record-btn"
                  >
                    <Mic size={14} /> Record now
                  </motion.button>
                ) : (
                  <motion.button
                    onClick={stopRecording}
                    whileTap={{ scale: 0.97 }}
                    className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm"
                    style={{
                      background: 'rgba(200,80,80,0.15)',
                      border: '1px solid rgba(220,100,100,0.4)',
                      color: 'rgba(255,180,180,0.9)',
                      fontFamily: "'Manrope', sans-serif",
                    }}
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                    data-testid="family-stop-btn"
                  >
                    <Square size={12} fill="currentColor" /> Stop · {fmt(recordSeconds)}
                  </motion.button>
                )}

                <label
                  className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm cursor-pointer"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.6)',
                    fontFamily: "'Manrope', sans-serif",
                    opacity: busy || recording || !speakerName.trim() ? 0.4 : 1,
                    pointerEvents: busy || recording || !speakerName.trim() ? 'none' : 'auto',
                  }}
                  data-testid="family-upload-label"
                >
                  <Upload size={14} /> Upload MP3
                  <input type="file" accept="audio/*" onChange={handleFile} className="hidden" data-testid="family-upload-input" />
                </label>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {msg && (
          <motion.p
            key={msg}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-xs mt-2"
            style={{
              color: msgKind === 'error' ? 'rgba(230,150,150,0.9)'
                : msgKind === 'success' ? 'rgba(180,220,180,0.9)'
                : 'rgba(255,255,255,0.5)',
              fontFamily: "'Manrope', sans-serif",
            }}
            data-testid="family-msg"
          >
            {msg}
          </motion.p>
        )}
      </motion.div>
    </motion.div>
  );
};

// ─── Voice Row ───────────────────────────────────────────────────────
const VoiceRow = ({ voice, isSelected, isPlaying, onPreview, onSelect, onRemove, canSelect }) => (
  <motion.div
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-center gap-3 rounded-2xl px-4 py-3"
    style={{
      background: isSelected ? 'rgba(180,200,150,0.09)' : 'rgba(255,255,255,0.03)',
      border: `1px solid ${isSelected ? 'rgba(180,200,150,0.35)' : 'rgba(255,255,255,0.06)'}`,
    }}
    data-testid={`family-voice-row-${voice.id}`}
  >
    {/* Avatar / icon */}
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
      style={{
        background: voice.is_synthetic
          ? 'linear-gradient(135deg, rgba(200,170,110,0.3), rgba(160,120,60,0.2))'
          : 'rgba(200,170,110,0.15)',
        border: '1px solid rgba(200,170,110,0.3)',
        color: 'rgba(255,240,210,0.85)',
        fontFamily: "'Playfair Display', serif",
      }}
    >
      {voice.is_synthetic ? <Sparkles size={14} /> : (voice.speaker_name?.[0] || '?')}
    </div>

    {/* Details */}
    <div className="flex-1 min-w-0">
      <p className="text-white/85 text-sm truncate" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
        {voice.speaker_name || 'Unnamed'}
      </p>
      <p className="text-white/40 text-[11px] truncate flex items-center gap-1.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <span>{voice.relation || (voice.is_synthetic ? 'Studio voice' : 'Family')}</span>
        {(voice.refined_via_elevenlabs || voice.is_synthetic) && (
          <span
            className="text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-full"
            style={{
              background: 'rgba(200,170,110,0.15)',
              color: 'rgba(230,200,140,0.9)',
              border: '1px solid rgba(200,170,110,0.28)',
            }}
            title="Rendered as a smooth full-script MP3 via ElevenLabs"
            data-testid={`family-refined-badge-${voice.id}`}
          >
            Smooth
          </span>
        )}
        {!voice.refined_via_elevenlabs && !voice.is_synthetic && (
          <span
            className="text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-full"
            style={{
              background: 'rgba(180,220,255,0.08)',
              color: 'rgba(180,220,255,0.75)',
              border: '1px solid rgba(180,220,255,0.2)',
            }}
            title="Being polished by ElevenLabs — will be ready in ~1 min"
            data-testid={`family-refining-badge-${voice.id}`}
          >
            Refining…
          </span>
        )}
      </p>
    </div>

    {/* Actions */}
    <button
      onClick={onPreview}
      className="p-2 rounded-full hover:bg-white/5 text-white/60"
      title="Preview"
      data-testid={`family-preview-${voice.id}`}
    >
      {isPlaying ? <Pause size={14} /> : <Play size={14} />}
    </button>
    {canSelect && (
      <button
        onClick={onSelect}
        className="p-2 rounded-full text-white/60 hover:bg-white/5"
        title={isSelected ? 'Currently selected' : 'Use for today'}
        style={isSelected ? { color: 'rgba(180,220,180,0.9)' } : undefined}
        data-testid={`family-select-${voice.id}`}
      >
        {isSelected ? <Check size={14} /> : <div className="w-3 h-3 rounded-full border border-white/40" />}
      </button>
    )}
    {!voice.is_synthetic && (
      <button
        onClick={onRemove}
        className="p-2 rounded-full text-white/35 hover:text-red-300 hover:bg-red-500/10"
        title="Remove"
        data-testid={`family-remove-${voice.id}`}
      >
        <Trash2 size={13} />
      </button>
    )}
  </motion.div>
);

export default SpinalEaseFamilyCircle;
